import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { TextField, Typography, Grid, Button, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../utils/axios';
import { objectArrayMin } from '../../utils/math';
import CurrencyInputDefaults from '../../reuseable-components/CurrencyInput/CurrencyInputDefaults';
import { objectToFormData } from '../../utils/objectToFormData';


InvoiceItemForm.propTypes = {
  gridApi: PropTypes.object,
  invoiceItemNode: PropTypes.object,
  setInvoiceItemNode: PropTypes.func,
  setInvoiceItems: PropTypes.func,
  open: PropTypes.bool,
  close: PropTypes.func,
  formData: PropTypes.object
}

export default function InvoiceItemForm({ gridApi, invoiceItemNode, setInvoiceItemNode, setInvoiceItems, open, close, formData }) {
  const [formValue, setFormValue] = useState({});
  const [fetchingItemByVendorItemId, setFetchingItemByVendorItemId] = useState(false);
  const [vendorItemId, setVendorItemId] = useState("");

  const fetchItemByVendorItemId = () => {
    setFetchingItemByVendorItemId(true);
    axiosInstance.get(`product/productPricing`, {
      params: { facilityId: formData.facilityId, orderId: formData.orderNumber, vendorId: formData.vendorId, vendorItemId }
    })
      .then(res => {
        if (!res.data) {
          enqueueSnackbar('Product not found', { variant: 'warning' });
          return;
        }      
        const uom = res.data.uomTypes?.length ? res.data.uomTypes.find(uom => uom.price) ?? res.data.uomTypes[0] : null;      
        setFormValue(formData => ({
          ...formData,
          description: res.data.description,
          vendorItemId: res.data.vendorItemId,
          uomType: uom?.uomType,
          price: uom?.vendorPrice,
          customerPrice: uom?.customerPrice
        }));
        setVendorItemId("");
      })
      .catch(() => { enqueueSnackbar('Failed to fetch product', { variant: 'error' }); })
      .finally(() => setFetchingItemByVendorItemId(false));
  }

  useEffect(() => {
    setFormValue(invoiceItemNode?.data ? {
      ...invoiceItemNode.data,
      price: invoiceItemNode.data.price?.toFixed(2),
      customerPrice:
        typeof invoiceItemNode.data.customerPrice === "number"
          ? invoiceItemNode.data.customerPrice.toFixed(2)
          : "0.00",
      taxAmount: invoiceItemNode.data.taxAmount?.toFixed(2),
    } : {})
  }, [open, invoiceItemNode])  

  const submit = () => {
    setInvoiceItems(invoiceItems => {
      formValue.price = Number(formValue.price ?? 0);
      formValue.taxAmount = Number(formValue.taxAmount ?? 0);
      const custPrice = Number(formValue.customerPrice);
      formValue.customerPrice = !Number.isNaN(custPrice) ? custPrice : null;
      formValue.quantity = Number(formValue.quantity ?? 0);
      if (invoiceItemNode && typeof invoiceItemNode.setData === "function") {
        invoiceItemNode.setData(formValue);
        return [...invoiceItems.filter(item => item.id !== invoiceItemNode.data.id), formValue];
      }
      formValue.id = objectArrayMin(invoiceItems, "id") - 1;
      gridApi?.api.applyTransaction({ add: [formValue] });
      return [...invoiceItems, formValue];
    });
    close();
  }

  return <Box>
    <Grid container xl={12} spacing={2} >
      <Grid item xl={12}>
        <Typography variant="subtitle1">Description*</Typography>
        <TextField
          name="description"
          size="small"
          placeholder="Enter Description"
          fullWidth
          value={formValue.description ?? ""}
          onChange={(e) => setFormValue(form => ({ ...form, description: e.target.value }))}
        />
      </Grid>
      <Grid item xl={12} container spacing={2}>
        <Grid item xl={6}>
          <Box display="flex" alignItems="center" justifyContent="start">
            <Typography variant="subtitle1">Vendor Item Id*</Typography>
            <LoadingButton loading={fetchingItemByVendorItemId} variant="contained" size="small" disabled={!vendorItemId} onClick={fetchItemByVendorItemId}
              sx={{ ml: 1, px: 0.5, py: 0, fontSize: "0.75rem", minWidth: "auto" }} >
              search
            </LoadingButton>
          </Box>
          <TextField
            name="vendorItemId"
            size="small"
            placeholder="Enter Vendor Item Id"
            fullWidth
            value={formValue.vendorItemId ?? ""}
            onChange={(e) => {
              setFormValue(form => ({ ...form, vendorItemId: e.target.value }));
              setVendorItemId(e.target.value);
            }}
          />
        </Grid>
        <Grid item xl={6}>
          <Typography variant="subtitle1">UOM Type</Typography>
          <TextField
            name="uomType"
            size="small"
            placeholder="Enter UOM Type"
            fullWidth
            value={formValue.uomType ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, uomType: e.target.value }))}
          />
        </Grid>
      </Grid>
      <Grid item xl={12} container spacing={2}>
        <Grid item xl={3}>
          <Typography variant="subtitle1">Price*</Typography>
          <CurrencyInput
            {...CurrencyInputDefaults()}
            name="price"
            placeholder="Enter Price"
            value={formValue.price}
            onValueChange={(value) => setFormValue(form => ({ ...form, price: value }))}
          />
        </Grid>
        <Grid item xl={3}>
          <Typography variant="subtitle1">Customer Price*</Typography>
          <CurrencyInput
            {...CurrencyInputDefaults()}
            name="customerPrice"
            placeholder="Enter Customer Price"
            value={formValue.customerPrice}
            onValueChange={(value) => setFormValue(form => ({ ...form, customerPrice: value }))}
          />
        </Grid>
        <Grid item xl={3}>
          <Typography variant="subtitle1">Tax Amount</Typography>
          <CurrencyInput
            {...CurrencyInputDefaults()}
            name="taxAmount"
            placeholder="Enter Tax Amount"
            value={formValue.taxAmount}
            onValueChange={(value) => setFormValue(form => ({ ...form, taxAmount: value }))}
          />
        </Grid>
        <Grid item xl={3}>
          <Typography variant="subtitle1">Quantity</Typography>
          <TextField
            name="quantity"
            size="small"
            placeholder="Enter Quantity"
            fullWidth
            value={formValue.quantity ?? ""}
            onChange={(e) => setFormValue(form => ({ ...form, quantity: Number(e.target.value?.replace(/[^0-9]/g, '') ?? 0) }))}
          />
        </Grid>
      </Grid>
      <Grid item xl={12} display="flex" justifyContent="center" mb={2}>
        <Button disabled={!formValue?.vendorItemId || !formValue?.description || formValue?.price < 0 || formValue?.customerPrice < 0} sx={{ px: 7, mr: 2 }}
          color="purple" size="medium" variant="contained" onClick={submit}>
          Save
        </Button>
        <Button sx={{ px: 6 }} color="error" size="medium" variant="outlined" onClick={close}>
          Cancel
        </Button>
      </Grid>
    </Grid>
  </Box>;
}